'use client';

import { logger } from '@/utils/logger';

interface TokenData {
  token: string;
  expiresAt: number;
}

export class CsrfTokenManager {
  private static instance: CsrfTokenManager;
  private tokenData: TokenData | null = null;
  private tokenPromise: Promise<string> | null = null;
  private retryCount = 0;
  private maxRetries = 3;
  private refreshTimeout: number | null = null;
  private csrfEndpoint = '/api/csrf-token';

  private constructor() {}

  public static getInstance(): CsrfTokenManager {
    if (!CsrfTokenManager.instance) {
      CsrfTokenManager.instance = new CsrfTokenManager();
    }
    return CsrfTokenManager.instance;
  }

  public async getToken(forceRefresh = false): Promise<string> {
    if (this.tokenData && !this.isTokenExpired() && !forceRefresh) {
      return this.tokenData.token;
    }

    if (this.tokenPromise && !forceRefresh) {
      return this.tokenPromise;
    }

    if (forceRefresh) {
      this.retryCount = 0;
    }

    this.tokenPromise = this.fetchCsrfToken();
    
    try {
      const token = await this.tokenPromise;
      this.scheduleTokenRefresh();
      return token;
    } finally {
      this.tokenPromise = null;
    }
  }

  public async refreshToken(): Promise<string> {
    return this.getToken(true);
  }

  private isTokenExpired(): boolean {
    if (!this.tokenData) return true;
    return Date.now() >= this.tokenData.expiresAt - 60000; // Refresh 1 min before expiry
  }

  private async fetchCsrfToken(): Promise<string> {
    if (this.retryCount >= this.maxRetries) {
      throw new Error('Maximum retry attempts reached. Please refresh the page and try again.');
    }

    try {
      const response = await fetch(this.csrfEndpoint, {
        credentials: 'include', // Important for cookies
      });

      if (!response.ok) {
        throw new Error('Failed to fetch CSRF token');
      }

      const data = await response.json();
      this.tokenData = {
        token: data.token,
        expiresAt: data.expiresAt
      };
      
      return data.token;
    } catch (error) {
      logger.error(`CSRF token fetch attempt ${this.retryCount + 1} failed`, { error });
      this.retryCount++;

      if (this.retryCount < this.maxRetries) {
        const delay = 1000 * Math.pow(2, this.retryCount);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.fetchCsrfToken();
      }

      throw new Error('Failed to retrieve security token. Please refresh the page and try again.');
    }
  }

  private scheduleTokenRefresh(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    if (!this.tokenData) return;

    const timeUntilRefresh = this.tokenData.expiresAt - Date.now() - 60000;
    if (timeUntilRefresh <= 0) return;

    this.refreshTimeout = window.setTimeout(async () => {
      try {
        await this.getToken(true);
      } catch (error) {
        logger.error('Failed to refresh CSRF token', { error });
      }
    }, timeUntilRefresh);
  }

  public cleanup(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
  }
}

// Create and export a singleton instance
const csrfTokenManager = CsrfTokenManager.getInstance();
export { csrfTokenManager };
